import React, { Fragment, Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';

import ProtectedRoute from '../ProtectedRoute';
import PublicRoutes from '../PublicRoutes';
import AreaRedirect from '../AreaRedirect';
import UserProvider from '../../providers/UserProvider';
import MyLoadingOverlay from '../../components/MyLoadingOverlay';
import NotFound from '../../pages/NotFound';
import { getArea } from '../../common/areas';
import { ABILITIES } from '../../common/permissions';

const Login = lazy(() => import('../../pages/Login'));
const EsqueciSenha = lazy(() => import('../../pages/EsqueciSenha'));
const RedefinirSenha = lazy(() => import('../../pages/RedefinirSenha'));
const Home = lazy(() => import('../../pages/Home'));
const PeliculaEdit = lazy(() => import('../../pages/Pelicula/Edit'));
const Catalogo = lazy(() => import('../../pages/Catalogo'));
const CatalogoCreate = lazy(() => import('../../pages/Catalogo/Create'));
const CatalogoDownload = lazy(() => import('../../pages/Catalogo/Download'));
const CatalogoRevista = lazy(() => import('../../pages/Catalogo/Revista'));
const CatalogoIngresso = lazy(() => import('../../pages/Catalogo/Ingresso'));
const PremiereSelecao = lazy(() => import('../../pages/Premiere/Selecao'));
const PremiereTodos = lazy(() => import('../../pages/Premiere/Todos'));
const PremiereMateriais = lazy(() => import('../../pages/Premiere/Materiais'));
const PremiereFinal = lazy(() => import('../../pages/Premiere/Final'));
const PremiereImprensa = lazy(() => import('../../pages/Premiere/Imprensa'));
const PremiereRelease = lazy(() => import('../../pages/Premiere/Release'));
const Projecao = lazy(() => import('../../pages/Projecao'));
const InternacionalTodos = lazy(() =>
  import('../../pages/Internacional/Todos'),
);
const InternacionalScreeners = lazy(() =>
  import('../../pages/Internacional/Screeners'),
);
const InternacionalMateriais = lazy(() =>
  import('../../pages/Internacional/Materiais'),
);
const InternacionalFinal = lazy(() =>
  import('../../pages/Internacional/Final'),
);
const InternacionalImprensa = lazy(() =>
  import('../../pages/Internacional/Imprensa'),
);
const ProgramacaoPelicula = lazy(() =>
  import('../../pages/Programacao/Pelicula'),
);
const ProgramacaoPeliculaEdit = lazy(() =>
  import('../../pages/Programacao/Pelicula/Edit'),
);
const ProgramacaoProgramacoes = lazy(() =>
  import('../../pages/Programacao/Programacoes'),
);
const ProgramacaoRenda = lazy(() => import('../../pages/Programacao/Renda'));
const ProgramacoesImprensa = lazy(() =>
  import('../../pages/Programacao/Imprensa'),
);
const Users = lazy(() => import('../../pages/Users'));
const UserCreate = lazy(() => import('../../pages/Users/Create'));
const UserEdit = lazy(() => import('../../pages/Users/Edit'));
const AlterarSenha = lazy(() => import('../../pages/AlterarSenha'));
const AlterarImagem = lazy(() => import('../../pages/AlterarImagem'));
const Mostras = lazy(() => import('../../pages/Mostras'));
const MostraCreate = lazy(() => import('../../pages/Mostras/Create'));
const MostraEdit = lazy(() => import('../../pages/Mostras/Edit'));
const SubMostras = lazy(() => import('../../pages/SubMostras'));
const SubMostraCreate = lazy(() => import('../../pages/SubMostras/Create'));
const SubMostraEdit = lazy(() => import('../../pages/SubMostras/Edit'));
const Cinemas = lazy(() => import('../../pages/Cinemas'));
const CinemaCreate = lazy(() => import('../../pages/Cinemas/Create'));
const CinemaEdit = lazy(() => import('../../pages/Cinemas/Edit'));
const Categorias = lazy(() => import('../../pages/Categorias'));
const CategoriaCreate = lazy(() => import('../../pages/Categorias/Create'));
const CategoriaEdit = lazy(() => import('../../pages/Categorias/Edit'));
const CategoriasConvidado = lazy(() =>
  import('../../pages/CategoriasConvidado'),
);
const Vinhetas = lazy(() => import('../../pages/Vinhetas'));
const VinhetaCreate = lazy(() => import('../../pages/Vinhetas/Create'));
const VinhetaEdit = lazy(() => import('../../pages/Vinhetas/Edit'));
const TelasDescanso = lazy(() => import('../../pages/TelasDescanso'));
const TelaDescansoCreate = lazy(() =>
  import('../../pages/TelasDescanso/Create'),
);
const TelaDescansoEdit = lazy(() => import('../../pages/TelasDescanso/Edit'));
const TiposIngresso = lazy(() => import('../../pages/TiposIngresso'));
const Languages = lazy(() => import('../../pages/Idiomas'));
const LanguageCreate = lazy(() => import('../../pages/Idiomas/Create'));
const LanguageEdit = lazy(() => import('../../pages/Idiomas/Edit'));
const Directors = lazy(() => import('../../pages/Diretores'));
const DirectorCreate = lazy(() => import('../../pages/Diretores/Create'));
const DirectorEdit = lazy(() => import('../../pages/Diretores/Edit'));
const Elenco = lazy(() => import('../../pages/Elenco'));
const ElencoCreate = lazy(() => import('../../pages/Elenco/Create'));
const ElencoEdit = lazy(() => import('../../pages/Elenco/Edit'));
const Roteiristas = lazy(() => import('../../pages/Roteiristas'));
const RoteiristaCreate = lazy(() => import('../../pages/Roteiristas/Create'));
const RoteiristaEdit = lazy(() => import('../../pages/Roteiristas/Edit'));
const Distribuidoras = lazy(() => import('../../pages/Distribuidoras'));
const DistribuidoraCreate = lazy(() =>
  import('../../pages/Distribuidoras/Create'),
);
const DistribuidoraEdit = lazy(() => import('../../pages/Distribuidoras/Edit'));
const DistribuidorasAtribuidas = lazy(() =>
  import('../../pages/DistribuidorasAtribuidas'),
);
const DistribuidoraAtribuidaCreate = lazy(() =>
  import('../../pages/DistribuidorasAtribuidas/Create'),
);
const DistribuidoraAtribuidaEdit = lazy(() =>
  import('../../pages/DistribuidorasAtribuidas/Edit'),
);
const Produtoras = lazy(() => import('../../pages/Produtoras'));
const ProdutoraCreate = lazy(() => import('../../pages/Produtoras/Create'));
const ProdutoraEdit = lazy(() => import('../../pages/Produtoras/Edit'));
const Produtores = lazy(() => import('../../pages/Produtores'));
const ProdutorCreate = lazy(() => import('../../pages/Produtores/Create'));
const ProdutorEdit = lazy(() => import('../../pages/Produtores/Edit'));
const Fotografos = lazy(() => import('../../pages/Fotografos'));
const FotografoCreate = lazy(() => import('../../pages/Fotografos/Create'));
const FotografoEdit = lazy(() => import('../../pages/Fotografos/Edit'));
const Editores = lazy(() => import('../../pages/Editores'));
const EditorCreate = lazy(() => import('../../pages/Editores/Create'));
const EditorEdit = lazy(() => import('../../pages/Editores/Edit'));
const Musicos = lazy(() => import('../../pages/Musicos'));
const MusicoCreate = lazy(() => import('../../pages/Musicos/Create'));
const MusicoEdit = lazy(() => import('../../pages/Musicos/Edit'));
const DesenhistaDeSom = lazy(() => import('../../pages/DesenhistaDeSom'));
const DesenhistaDeSomCreate = lazy(() =>
  import('../../pages/DesenhistaDeSom/Create'),
);
const DesenhistaDeSomEdit = lazy(() =>
  import('../../pages/DesenhistaDeSom/Edit'),
);
const ConfiguracoesGerais = lazy(() =>
  import('../../pages/ConfiguracoesGerais'),
);
const ConvidadosEventos = lazy(() => import('../../pages/Convidados/Eventos'));
const ConvidadosEventosCreate = lazy(() =>
  import('../../pages/Convidados/Eventos/Create'),
);
const ConvidadosEventosEdit = lazy(() =>
  import('../../pages/Convidados/Eventos/Edit'),
);
const Hoteis = lazy(() => import('../../pages/Hoteis'));
const CadastrarHoteis = lazy(() => import('../../pages/Hoteis/Create'));
const EditarHoteis = lazy(() => import('../../pages/Hoteis/Edit'));
const ConvidadosListaGeral = lazy(() => import('../../pages/Convidados/Geral'));
const ConvidadosCadastrar = lazy(() => import('../../pages/Convidados/Create'));
const ConvidadosEditar = lazy(() => import('../../pages/Convidados/Edit'));
const ConvidadosHospedagens = lazy(() =>
  import('../../pages/Convidados/Hospedagens'),
);
const ConvidadosViagens = lazy(() => import('../../pages/Convidados/Viagens'));
const CredenciamentoGeral = lazy(() =>
  import('../../pages/Credenciamento/Geral'),
);
const CredenciamentoPremiere = lazy(() =>
  import('../../pages/Credenciamento/Premiere'),
);
const CredenciamentoImprensa = lazy(() =>
  import('../../pages/Credenciamento/Imprensa'),
);
const CredenciamentoEquipe = lazy(() =>
  import('../../pages/Credenciamento/Equipe'),
);
const CredenciamentoRiomarket = lazy(() =>
  import('../../pages/Credenciamento/Riomarket'),
);
const CredenciamentoAcessoEspecial = lazy(() =>
  import('../../pages/Credenciamento/AcessoEspecial'),
);
const Apoios = lazy(() => import('../../pages/Apoios'));
const CadastrarApoios = lazy(() => import('../../pages/Apoios/Create'));
const EditarApoios = lazy(() => import('../../pages/Apoios/Edit'));

// Remonta a tela quando o :id do endereço muda (ex.: busca rápida da ficha do
// filme levando a outro filme). As fichas só carregam os dados ao abrir.
function RemountOnId({ children }) {
  const { id } = useParams();
  return <Fragment key={id}>{children}</Fragment>;
}

function MainRoutes() {
  const area = getArea();

  if (area !== 'sistema') {
    return (
      <Suspense fallback={<MyLoadingOverlay />}>
        <Router>
          <UserProvider>
            <PublicRoutes area={area} />
          </UserProvider>
        </Router>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<MyLoadingOverlay />}>
      <Router>
        <UserProvider>
          <Routes>
            {/* Enderecos antigos das telas publicas, que agora moram nos
                dominios proprios (src/common/areas.js). */}
            <Route
              path="/credenciamento/filme/*"
              element={
                <AreaRedirect area="credenciamento" from="/credenciamento" />
              }
            />
            <Route
              path="/credenciamento/imprensa/inscricao"
              exact
              element={
                <AreaRedirect area="credenciamento" from="/credenciamento" />
              }
            />
            <Route
              path="/credenciamento/imprensa/sucesso"
              exact
              element={
                <AreaRedirect area="credenciamento" from="/credenciamento" />
              }
            />
            <Route
              path="/filme/:id"
              exact
              element={<AreaRedirect area="filmes" from="/filme" />}
            />
            <Route path="*" element={<NotFound />} />
            <Route path="/" exact element={<Login />} />
            {/* "Esqueceu sua senha?" do login: sem login, como ele */}
            <Route path="/esqueci-a-senha" exact element={<EsqueciSenha />} />
            <Route
              path="/redefinir-senha/:token"
              exact
              element={<RedefinirSenha />}
            />
            <Route
              path="/dashboard"
              exact
              element={
                <ProtectedRoute ability={ABILITIES.DASHBOARD}>
                  <Home />
                </ProtectedRoute>
              }
            />
            {/* O Catálogo é uma tela só para as quatro listagens do menu: o
                `key` de cada rota faz a troca de item abrir a tela do zero,
                sem a lista, os filtros e a página da listagem anterior (a
                lista ali só vem com o Buscar). */}
            <Route
              path="/lista-catalogo-premiere-brasil"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <Catalogo
                    key="list-catalogo-premiere-brasil"
                    filter="premiere"
                    submenu="list-catalogo-premiere-brasil"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lista-catalogo-filmes-internacionais"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <Catalogo
                    key="list-catalogo-internacionais"
                    filter="internacionais"
                    submenu="list-catalogo-internacionais"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/todos-filmes-catalogo-premiere-brasil"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <Catalogo
                    key="list-catalogo-todos-pb"
                    filter="premiere_all"
                    submenu="list-catalogo-todos-pb"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/todos-filmes-catalogo-internacional"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <Catalogo
                    key="list-catalogo-todos-int"
                    filter="internacionais_all"
                    submenu="list-catalogo-todos-int"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalogo/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <CatalogoCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalogo/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <RemountOnId>
                    <PeliculaEdit />
                  </RemountOnId>
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalogo/download/:id"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <CatalogoDownload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalogo/conteudo/revista"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <CatalogoRevista />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projecao"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <Projecao />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premiere/todos"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <PremiereTodos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premiere/selecao"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <PremiereSelecao />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premiere/materiais"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <PremiereMateriais />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premiere/final"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <PremiereFinal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premiere/imprensa"
              exact
              element={
                <ProtectedRoute ability={ABILITIES.PREMIERE_PRESS_LIST}>
                  <PremiereImprensa />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premiere/release"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <PremiereRelease />
                </ProtectedRoute>
              }
            />
            <Route
              path="/internacional/todos"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <InternacionalTodos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/internacional/screeners"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <InternacionalScreeners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/internacional/materiais"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <InternacionalMateriais />
                </ProtectedRoute>
              }
            />
            <Route
              path="/internacional/final"
              exact
              element={
                <ProtectedRoute roles={[1, 2]}>
                  <InternacionalFinal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/internacional/imprensa"
              exact
              element={
                <ProtectedRoute ability={ABILITIES.INTERNATIONAL_PRESS_LIST}>
                  <InternacionalImprensa />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programacao/pelicula"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ProgramacaoPelicula />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programacao/pelicula/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <RemountOnId>
                    <ProgramacaoPeliculaEdit />
                  </RemountOnId>
                </ProtectedRoute>
              }
            />
            <Route
              path="/programacao/programacoes"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ProgramacaoProgramacoes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programacao/projecao/exportar"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CatalogoIngresso />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programacao/renda"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ProgramacaoRenda />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programacao/imprensa"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ProgramacoesImprensa />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/eventos"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ConvidadosEventos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/eventos/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ConvidadosEventosCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/eventos/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ConvidadosEventosEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/geral"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ConvidadosListaGeral />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/cadastrar"
              exact
              element={
                <ProtectedRoute
                  roles={[1, 2, 3]}
                  ability={ABILITIES.PRESS_CREDENTIALS}
                >
                  <ConvidadosCadastrar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/editar/:id"
              exact
              element={
                <ProtectedRoute
                  roles={[1, 2, 3]}
                  ability={ABILITIES.PRESS_CREDENTIALS}
                >
                  <ConvidadosEditar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/hospedagens"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ConvidadosHospedagens />
                </ProtectedRoute>
              }
            />
            <Route
              path="/convidados/viagens"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <ConvidadosViagens />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credenciamento/geral"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CredenciamentoGeral />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credenciamento/premiere-brasil"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CredenciamentoPremiere />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credenciamento/imprensa"
              exact
              element={
                <ProtectedRoute
                  roles={[1, 2, 3]}
                  ability={ABILITIES.PRESS_CREDENTIALS}
                >
                  <CredenciamentoImprensa />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credenciamento/equipe"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CredenciamentoEquipe />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credenciamento/riomarket"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CredenciamentoRiomarket />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credenciamento/acesso-especial"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CredenciamentoAcessoEspecial />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hoteis"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Hoteis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hoteis/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <CadastrarHoteis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hoteis/editar/:id"
              exact
              element={
                <ProtectedRoute>
                  <EditarHoteis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <UserCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios/editar/:id"
              exact
              element={
                <ProtectedRoute>
                  <UserEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alterar-senha"
              exact
              element={
                <ProtectedRoute anyRole>
                  <AlterarSenha />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alterar-imagem"
              exact
              element={
                <ProtectedRoute anyRole>
                  <AlterarImagem />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mostras"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Mostras />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mostras/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <MostraCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mostras/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <MostraEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/submostras"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <SubMostras />
                </ProtectedRoute>
              }
            />
            <Route
              path="/submostras/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <SubMostraCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/submostras/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <SubMostraEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categorias"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Categorias />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categorias/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <CategoriaCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categorias/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <CategoriaEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categorias-convidado"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <CategoriasConvidado />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vinhetas"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Vinhetas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vinhetas/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <VinhetaCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vinhetas/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <VinhetaEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/telas-de-descanso"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <TelasDescanso />
                </ProtectedRoute>
              }
            />
            <Route
              path="/telas-de-descanso/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <TelaDescansoCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/telas-de-descanso/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <TelaDescansoEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tipos-de-ingresso"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <TiposIngresso />
                </ProtectedRoute>
              }
            />
            <Route
              path="/idiomas"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Languages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/idiomas/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <LanguageCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/idiomas/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <LanguageEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cinemas"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <Cinemas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cinemas/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CinemaCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cinemas/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1, 2, 3]}>
                  <CinemaEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/diretores"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Directors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/diretores/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DirectorCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/diretores/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DirectorEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/elenco"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Elenco />
                </ProtectedRoute>
              }
            />
            <Route
              path="/elenco/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <ElencoCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/elenco/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <ElencoEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roteiristas"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Roteiristas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roteiristas/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <RoteiristaCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roteiristas/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <RoteiristaEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/distribuidoras"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Distribuidoras />
                </ProtectedRoute>
              }
            />
            <Route
              path="/distribuidoras/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DistribuidoraCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/distribuidoras/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DistribuidoraEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/distribuidoras-atribuidas"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DistribuidorasAtribuidas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/distribuidoras-atribuidas/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DistribuidoraAtribuidaCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/distribuidoras-atribuidas/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DistribuidoraAtribuidaEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtoras"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Produtoras />
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtoras/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <ProdutoraCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtoras/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <ProdutoraEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtores"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Produtores />
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtores/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <ProdutorCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/produtores/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <ProdutorEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fotografos"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Fotografos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fotografos/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <FotografoCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fotografos/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <FotografoEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editores"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Editores />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editores/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <EditorCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editores/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <EditorEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/musicos"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Musicos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/musicos/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <MusicoCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/musicos/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <MusicoEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/som"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DesenhistaDeSom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/som/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DesenhistaDeSomCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/som/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <DesenhistaDeSomEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/apoios"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <Apoios />
                </ProtectedRoute>
              }
            />
            <Route
              path="/apoios/cadastrar"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <CadastrarApoios />
                </ProtectedRoute>
              }
            />
            <Route
              path="/apoios/editar/:id"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <EditarApoios />
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuracoes/gerais"
              exact
              element={
                <ProtectedRoute roles={[1]}>
                  <ConfiguracoesGerais />
                </ProtectedRoute>
              }
            />
          </Routes>
        </UserProvider>
      </Router>
    </Suspense>
  );
}

export default MainRoutes;
